/* eslint-disable react/forbid-component-props */
import { ComponentProps } from 'react';
import {
    Document,
    Font,
    Image,
    Link,
    Page,
    Text,
    View,
} from '@react-pdf/renderer';
import {
    InvoiceImageSize,
    fitImageBox,
    footerImageBox,
    invoiceImageWidth,
} from '../invoiceImages';
import { Invoice, InvoiceAcceptance, InvoiceImageWidth } from '../types';
import {
    SIGNATURE_MAX_HEIGHT,
    SIGNATURE_MAX_WIDTH,
    createInvoicePdfStyles,
    invoicePdfContentWidth,
} from './styles';
import PrestigeInvoiceDocument from './prestigeDocument';
import { isPrestigeInvoiceAgent } from './prestige';
import {
    getInvoicePdfModel,
    InvoicePdfModel,
    InvoicePdfTextSpan,
    parseMarkdownSpans,
} from './viewModel';

Font.registerHyphenationCallback((word) => [word]);

const markdownStyle = (
    span: InvoicePdfTextSpan,
    styles: ReturnType<typeof createInvoicePdfStyles>,
) => {
    if (span.bold) return styles.markdownBold;
    if (span.italic) return styles.markdownItalic;
    return undefined;
};

const MarkdownText = ({
    markdown,
    styles,
}: {
    markdown: string;
    styles: ReturnType<typeof createInvoicePdfStyles>;
}) => (
    <View>
        {parseMarkdownSpans(markdown).map((line) => (
            <Text key={line[0].key}>
                {line.map((span) => {
                    if (span.href) {
                        return (
                            // react-pdf Link uses `src`, not `href`.
                            // eslint-disable-next-line jsx-a11y/anchor-is-valid
                            <Link key={span.key} src={span.href}>
                                {span.text}
                            </Link>
                        );
                    }
                    return (
                        <Text key={span.key} style={markdownStyle(span, styles)}>
                            {span.text}
                        </Text>
                    );
                })}
            </Text>
        ))}
    </View>
);

const footerBlockStyle = (
    alignment: 'left' | 'center' | 'right',
    styles: ReturnType<typeof createInvoicePdfStyles>,
) => {
    if (alignment === 'center') return [styles.footerBlock, styles.footerBlockCenter];
    if (alignment === 'right') return [styles.footerBlock, styles.footerBlockRight];
    return styles.footerBlock;
};

const footerTextStyle = (
    alignment: 'left' | 'center' | 'right',
    styles: ReturnType<typeof createInvoicePdfStyles>,
) => {
    if (alignment === 'center') return [styles.footerText, styles.footerTextCenter];
    if (alignment === 'right') return [styles.footerText, styles.footerTextRight];
    return styles.footerText;
};

const cellBoxStyle = (
    width: number,
    fullWidth: boolean,
    styles: ReturnType<typeof createInvoicePdfStyles>,
) => {
    if (fullWidth) return [styles.tableCell, styles.tableCellFull];
    return [styles.tableCell, { width }];
};

const headerBoxStyle = (
    width: number,
    styles: ReturnType<typeof createInvoicePdfStyles>,
) => [styles.tableHeaderCell, { width }];

const headerTextStyle = (
    align: 'left' | 'right',
    styles: ReturnType<typeof createInvoicePdfStyles>,
) => (
    align === 'right'
        ? [styles.tableHeaderText, styles.tableHeaderTextRight]
        : styles.tableHeaderText
);

const bodyTextStyle = (
    align: 'left' | 'right',
    styles: ReturnType<typeof createInvoicePdfStyles>,
) => (
    align === 'right'
        ? [styles.tableCellText, styles.tableCellTextRight]
        : styles.tableCellText
);

const SellerLines = ({
    name,
    lines,
    styles,
}: {
    name: string;
    lines: string[];
    styles: ReturnType<typeof createInvoicePdfStyles>;
}) => (
    <View style={styles.seller}>
        {name ? <Text style={styles.sellerName}>{name}</Text> : null}
        {lines.map((line, index) => (
            <Text
                // eslint-disable-next-line react/no-array-index-key
                key={`${index}-${line}`}
                style={styles.sellerLine}
            >
                {line}
            </Text>
        ))}
    </View>
);

const fittedImageStyle = (
    imageUrl: string,
    imageSizes: Record<string, InvoiceImageSize>,
    containerWidth: number,
    size?: InvoiceImageWidth,
): ComponentProps<typeof Image>['style'] => {
    const measured = imageSizes[imageUrl];
    const box = measured
        ? footerImageBox(measured.width, measured.height, containerWidth, size)
        : undefined;
    if (box) {
        return {
            width: box.width,
            height: box.height,
        };
    }
    if (!size || containerWidth <= 0) return undefined;
    return { width: invoiceImageWidth(containerWidth, size) };
};

const InvoiceBody = ({
    model,
    styles,
    imageSizes,
    imageMaxWidth,
}: {
    model: InvoicePdfModel;
    styles: ReturnType<typeof createInvoicePdfStyles>;
    imageSizes: Record<string, InvoiceImageSize>;
    imageMaxWidth: number;
}) => (
    <View style={styles.main}>
        <View style={styles.header}>
            <View style={styles.brand}>
                {model.logoStyle === 'image' && model.logoImageUrl ? (
                    <Image
                        src={model.logoImageUrl}
                        style={fittedImageStyle(
                            model.logoImageUrl,
                            imageSizes,
                            imageMaxWidth,
                            model.logoSize,
                        )}
                    />
                ) : null}
                {model.logoStyle === 'wordmark' && model.wordmark ? (
                    <Text style={styles.wordmark}>{model.wordmark}</Text>
                ) : null}
                {model.sellerName || model.sellerLines.length > 0 ? (
                    <SellerLines
                        name={model.sellerName}
                        lines={model.sellerLines}
                        styles={styles}
                    />
                ) : null}
            </View>
            <View style={styles.heading}>
                <Text style={styles.title}>{model.title}</Text>
                {model.numberLine ? (
                    <Text style={styles.number}>{model.numberLine}</Text>
                ) : null}
                {model.signatureStatus ? (
                    <Text style={styles.signatureStatus}>{model.signatureStatus}</Text>
                ) : null}
            </View>
        </View>
        <View style={styles.meta}>
            <View style={styles.metaBillTo}>
                <Text style={styles.label}>{model.billToLabel}</Text>
                {model.buyerName ? (
                    <Text style={styles.buyerName}>{model.buyerName}</Text>
                ) : null}
                {model.buyerDetails.map((line, index) => (
                    <Text
                        // eslint-disable-next-line react/no-array-index-key
                        key={`${index}-${line}`}
                        style={styles.value}
                    >
                        {line}
                    </Text>
                ))}
            </View>
            {model.showIssuedOn ? (
                <View style={styles.metaDate}>
                    <Text style={styles.dateLabel}>{model.issuedOnLabel}</Text>
                    <Text style={styles.dateValue}>{model.issuedOn}</Text>
                </View>
            ) : null}
            {model.showDueOn ? (
                <View style={styles.metaDate}>
                    <Text style={styles.dateLabel}>{model.dueOnLabel}</Text>
                    <Text style={styles.dateValue}>{model.dueOn}</Text>
                </View>
            ) : null}
        </View>
        <View style={styles.table}>
            <View style={styles.tableHeader}>
                {model.columnLabels.map((label, index) => (
                    <View
                        key={label}
                        style={headerBoxStyle(model.columnWidths[index] || 40, styles)}
                    >
                        <Text style={headerTextStyle(model.columnAligns[index], styles)}>
                            {label}
                        </Text>
                    </View>
                ))}
            </View>
            {model.rows.map((row) => (
                <View key={row.key} style={styles.tableRow}>
                    {row.cells.map((cell, index) => {
                        const kind = model.columnKinds[index];
                        return (
                            <View
                                key={`${row.key}-${model.columnLabels[index] || index}`}
                                style={cellBoxStyle(
                                    model.columnWidths[index] || 40,
                                    row.cells.length === 1,
                                    styles,
                                )}
                            >
                                <Text
                                    wrap={kind === 'description'}
                                    style={bodyTextStyle(
                                        model.columnAligns[index] || 'left',
                                        styles,
                                    )}
                                >
                                    {cell}
                                </Text>
                            </View>
                        );
                    })}
                </View>
            ))}
        </View>
        <View style={styles.totals} wrap={false}>
            <View style={styles.totalRow}>
                <Text>Subtotal</Text>
                <Text>{model.subtotal}</Text>
            </View>
            {model.fees.map((fee) => (
                <View key={fee.key} style={styles.totalRow}>
                    <Text>{fee.label}</Text>
                    <Text>{fee.amount}</Text>
                </View>
            ))}
            <View style={[styles.totalRow, styles.grandTotal]}>
                <Text>Total</Text>
                <Text>{model.total}</Text>
            </View>
        </View>
        {model.note ? (
            <View style={styles.note} wrap={false}>
                <Text style={styles.noteTitle}>Note</Text>
                <Text style={styles.value}>{model.note}</Text>
            </View>
        ) : null}
    </View>
);

const signatureImageStyle = (
    imageUrl: string,
    imageSizes: Record<string, InvoiceImageSize>,
    styles: ReturnType<typeof createInvoicePdfStyles>,
): ComponentProps<typeof Image>['style'] => {
    const measured = imageSizes[imageUrl];
    const box = measured
        ? fitImageBox(
            measured.width,
            measured.height,
            SIGNATURE_MAX_WIDTH,
            SIGNATURE_MAX_HEIGHT,
        )
        : undefined;
    if (!box) return styles.signatureImage;
    return [styles.signatureImage, { width: box.width, height: box.height }];
};

const SignatureBlock = ({
    model,
    styles,
    imageSizes,
}: {
    model: InvoicePdfModel;
    styles: ReturnType<typeof createInvoicePdfStyles>;
    imageSizes: Record<string, InvoiceImageSize>;
}) => {
    if (model.signatureState !== 'signed') return null;
    return (
        // Keep the block intact. It stays on the current page when it fits.
        <View style={styles.signature} wrap={false}>
            <Text style={styles.signatureLabel}>Customer acceptance</Text>
            {model.signatureAgreement ? (
                <Text style={styles.signatureAgreement}>{model.signatureAgreement}</Text>
            ) : null}
            <View style={styles.signatureMark}>
                {model.signatureImageUrl ? (
                    <Image
                        src={model.signatureImageUrl}
                        style={signatureImageStyle(model.signatureImageUrl, imageSizes, styles)}
                    />
                ) : null}
                {!model.signatureImageUrl && model.signatureName ? (
                    <Text style={styles.signatureName}>{model.signatureName}</Text>
                ) : null}
            </View>
            <View style={styles.signatureMetaRow}>
                {model.signatureMethod ? (
                    <Text style={styles.signatureMeta}>{model.signatureMethod}</Text>
                ) : null}
                {model.signatureMethod && model.signatureDate ? (
                    <View style={styles.signatureMetaDivider} />
                ) : null}
                {model.signatureDate ? (
                    <Text style={styles.signatureMeta}>{`Accepted on ${model.signatureDate}`}</Text>
                ) : null}
            </View>
        </View>
    );
};

const FOOTER_TEXT_LINE = 14;
const FOOTER_BLOCK_GAP = 10;

const footerReserve = (
    model: InvoicePdfModel,
    imageSizes: Record<string, InvoiceImageSize>,
    imageMaxWidth: number,
) => {
    if (model.footerBlocks.length === 0) return 0;
    const blocksHeight = model.footerBlocks.reduce((sum, block) => {
        if (block.kind === 'image') {
            const measured = block.imageUrl ? imageSizes[block.imageUrl] : undefined;
            const box = measured
                ? footerImageBox(
                    measured.width,
                    measured.height,
                    imageMaxWidth,
                    block.size,
                )
                : undefined;
            return sum + (box?.height || 40) + FOOTER_BLOCK_GAP;
        }
        const lines = Math.max(1, block.markdown.split('\n').length);
        return sum + (lines * FOOTER_TEXT_LINE) + FOOTER_BLOCK_GAP;
    }, 0);
    return 16 + blocksHeight;
};

const InvoiceFooter = ({
    model,
    styles,
    imageSizes,
    imageMaxWidth,
}: {
    model: InvoicePdfModel;
    styles: ReturnType<typeof createInvoicePdfStyles>;
    imageSizes: Record<string, InvoiceImageSize>;
    imageMaxWidth: number;
}) => {
    if (model.footerBlocks.length === 0) return null;
    return (
        <View style={styles.footer} fixed>
            {model.footerBlocks.map((block) => (
                <View
                    key={block.id}
                    style={footerBlockStyle(block.alignment, styles)}
                >
                    {block.kind === 'text' ? (
                        <View style={footerTextStyle(block.alignment, styles)}>
                            <MarkdownText
                                markdown={block.markdown}
                                styles={styles}
                            />
                        </View>
                    ) : null}
                    {block.kind === 'image' && block.imageUrl ? (
                        <Image
                            src={block.imageUrl}
                            style={fittedImageStyle(
                                block.imageUrl,
                                imageSizes,
                                imageMaxWidth,
                                block.size,
                            )}
                        />
                    ) : null}
                </View>
            ))}
        </View>
    );
};

const createPdfOwnerPassword = () => {
    const bytes = crypto.getRandomValues(new Uint8Array(32));
    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
};

interface InvoicePdfDocumentProps {
    invoice: Invoice;
    acceptance?: InvoiceAcceptance | null;
    imageSizes?: Record<string, InvoiceImageSize>;
    agentId?: string;
}

const InvoicePdfDocument = ({
    invoice,
    acceptance = null,
    imageSizes = {},
    agentId = '',
}: InvoicePdfDocumentProps) => {
    if (isPrestigeInvoiceAgent(agentId)) {
        return (
            <PrestigeInvoiceDocument
                invoice={invoice}
                imageSizes={imageSizes}
            />
        );
    }
    const model = getInvoicePdfModel(invoice, acceptance);
    const styles = createInvoicePdfStyles(model);
    const imageMaxWidth = invoicePdfContentWidth(model);
    const reservedFooter = footerReserve(model, imageSizes, imageMaxWidth);
    const ownerPassword = createPdfOwnerPassword();

    return (
        <Document
            title={`${model.title} ${invoice.number}`.trim()}
            author={model.sellerName}
            ownerPassword={ownerPassword}
            permissions={{
                printing: 'highResolution',
                modifying: false,
                copying: false,
                annotating: false,
                fillingForms: false,
                contentAccessibility: true,
                documentAssembly: false,
            }}
        >
            <Page
                size={model.pdfPageSize}
                style={[styles.page, { paddingBottom: styles.page.paddingBottom + reservedFooter }]}
            >
                <View style={styles.accent} fixed />
                {model.isVoid ? (
                    <View style={styles.watermark} fixed>
                        <Text style={styles.watermarkText}>VOID</Text>
                    </View>
                ) : null}
                <InvoiceBody
                    model={model}
                    styles={styles}
                    imageSizes={imageSizes}
                    imageMaxWidth={imageMaxWidth}
                />
                <SignatureBlock model={model} styles={styles} imageSizes={imageSizes} />
                <InvoiceFooter
                    model={model}
                    styles={styles}
                    imageSizes={imageSizes}
                    imageMaxWidth={imageMaxWidth}
                />
            </Page>
        </Document>
    );
};

export default InvoicePdfDocument;
